import type { ReactNode } from 'react';
import { styles } from './styles';

/**
 * Sticky bottom action bar shown on viewports narrower than Tailwind's `lg`
 * breakpoint (1024px). Hides itself on larger screens via `lg:hidden` in the
 * style token.
 *
 * Two optional slots:
 * - `info`: a compact label/value pair on the left (e.g. "Price" / "$24–$48").
 * - `actions`: primary + secondary buttons on the right (e.g. "Edit Product").
 *
 * Either slot may be omitted — the bar still lays out cleanly when only one
 * side is filled.
 */
export function MobileStickyBar({
  info,
  actions,
}: {
  info?: ReactNode;
  actions?: ReactNode;
}): ReactNode {
  if (!info && !actions) return null;
  return (
    <div className={styles.mobileBar} role="region" aria-label="Quick actions">
      {info ? <div className={styles.mobileBarInfo}>{info}</div> : null}
      {actions ? <div className={styles.mobileBarActions}>{actions}</div> : null}
    </div>
  );
}

/**
 * Convenience pair of slot content for the info side. Renders a small label
 * above a bold value, both truncated to a single line.
 */
export function MobileBarInfo({ label, value }: { label: string; value: ReactNode }): ReactNode {
  return (
    <>
      <span className={styles.mobileBarLabel}>{label}</span>
      <span className={styles.mobileBarValue}>{value}</span>
    </>
  );
}